import React from 'react';
import { Space, Input, Select, Button, DatePicker } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import type { AdsListParams } from '../../services/adsService';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

export interface AdsFiltersValues
  extends Pick<
    AdsListParams,
    'search' | 'type' | 'status' | 'budgetState' | 'sortBy'
  > {
  dateRange?: [dayjs.Dayjs, dayjs.Dayjs] | null;
}

interface AdsFiltersProps {
  values: AdsFiltersValues;
  onChange: (values: AdsFiltersValues) => void;
  onSearch: () => void;
  onReset: () => void;
  loading?: boolean;
}

const TYPE_OPTIONS = [
  { value: '', label: 'Tất cả loại' },
  { value: 'SHOP', label: 'TOÀN SHOP' },
  { value: 'ITEM', label: 'MÓN ĂN' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Tất cả trạng thái' },
  { value: 'DRAFT', label: 'Nháp' },
  { value: 'ACTIVE', label: 'Đang chạy' },
  { value: 'PAUSED', label: 'Tạm dừng' },
  { value: 'ENDED', label: 'Đã kết thúc' },
];

const BUDGET_OPTIONS = [
  { value: '', label: 'Tất cả' },
  { value: 'healthy', label: 'Ổn định' },
  { value: 'near_limit', label: 'Gần hết ngân sách' },
  { value: 'out_of_budget', label: 'Hết ngân sách' },
];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Mới nhất' },
  { value: 'highest_cpso', label: 'CPSO cao nhất' },
  { value: 'highest_spend', label: 'Chi tiêu cao nhất' },
  { value: 'most_orders', label: 'Nhiều đơn nhất' },
];

export const AdsFilters: React.FC<AdsFiltersProps> = ({
  values,
  onChange,
  onSearch,
  onReset,
  loading,
}) => {
  return (
    <Space wrap size="middle" style={{ marginBottom: 16 }}>
      <Input
        placeholder="Tìm theo campaign ID, tên merchant, merchant ID..."
        prefix={<SearchOutlined />}
        value={values.search ?? ''}
        onChange={e => onChange({ ...values, search: e.target.value })}
        onPressEnter={onSearch}
        style={{ width: 280 }}
        allowClear
      />
      <Select
        placeholder="Loại"
        value={values.type ?? ''}
        onChange={v =>
          onChange({
            ...values,
            type: (v || undefined) as AdsFiltersValues['type'],
          })
        }
        style={{ width: 140 }}
        options={TYPE_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
      />
      <Select
        placeholder="Trạng thái"
        value={values.status ?? ''}
        onChange={v =>
          onChange({
            ...values,
            status: (v || undefined) as AdsFiltersValues['status'],
          })
        }
        style={{ width: 140 }}
        options={STATUS_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
      />
      <Select
        placeholder="Ngân sách"
        value={values.budgetState ?? ''}
        onChange={v =>
          onChange({
            ...values,
            budgetState: (v || undefined) as AdsFiltersValues['budgetState'],
          })
        }
        style={{ width: 160 }}
        options={BUDGET_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
      />
      <RangePicker
        placeholder={['Từ ngày', 'Đến ngày']}
        value={values.dateRange ?? undefined}
        onChange={range =>
          onChange({
            ...values,
            dateRange:
              range && range[0] && range[1]
                ? ([range[0], range[1]] as [dayjs.Dayjs, dayjs.Dayjs])
                : undefined,
          })
        }
        style={{ width: 240 }}
      />
      <Select
        placeholder="Sắp xếp"
        value={values.sortBy ?? 'newest'}
        onChange={v =>
          onChange({
            ...values,
            sortBy: (v || 'newest') as AdsFiltersValues['sortBy'],
          })
        }
        style={{ width: 160 }}
        options={SORT_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
      />
      <Button type="primary" onClick={onSearch} loading={loading}>
        Tìm kiếm
      </Button>
      <Button icon={<ReloadOutlined />} onClick={onReset}>
        Đặt lại
      </Button>
    </Space>
  );
};
